import { useState } from "react";
import { ApiError } from "../api";
import { Icon } from "./icons";

type State = { kind: "idle" } | { kind: "working" } | { kind: "suggestion"; text: string } | { kind: "error"; message: string };

/**
 * Improve for one text field: the button that asks for fuller coaching feedback, and
 * the suggestion under the field to use, try again or ignore. The coach's own
 * text is never replaced unless they choose the suggestion.
 */
export function useImprove(text: string, improve: (text: string) => Promise<string>, onUse: (text: string) => void) {
    const [state, setState] = useState<State>({ kind: "idle" });

    const run = async () => {
        if (!text.trim()) return;
        setState({ kind: "working" });
        try {
            setState({ kind: "suggestion", text: await improve(text) });
        } catch (err) {
            setState({ kind: "error", message: err instanceof ApiError ? err.message : "Improve didn't work this time. Your text is unchanged; try again." });
        }
    };

    const button = (disabled: boolean) => (
        <button type="button" className="improve-btn" onClick={() => void run()} disabled={disabled || !text.trim() || state.kind === "working"}
            title="Turn your note into detailed feedback: why it matters, how to fix it and a drill. You choose whether to use it.">
            {state.kind === "working" ? <span className="spinner small" aria-hidden /> : <Icon.sparkle />}
            {state.kind === "working" ? "Improving" : "Improve"}
        </button>
    );

    const panel = state.kind === "suggestion" ? (
        <div className="improve-panel" role="region" aria-label="Suggested feedback">
            <div className="improve-head"><Icon.sparkle /> Suggested feedback</div>
            <p className="improve-text">{state.text}</p>
            <div className="improve-actions">
                <button type="button" className="btn primary small" onClick={() => { onUse(state.text); setState({ kind: "idle" }); }}>Use this</button>
                <button type="button" className="btn small" onClick={() => void run()}>Try again</button>
                <button type="button" className="btn ghost small" onClick={() => setState({ kind: "idle" })}>Keep mine</button>
            </div>
        </div>
    ) : state.kind === "error" ? <p className="error-text small" role="alert">{state.message}</p> : null;

    return { button, panel };
}
